import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@repletoryreach/sdk-auth';
import { isApiError } from '@repletoryreach/sdk-api';
import { Stack, Button, Group, Text } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { TextInput } from '@repletoryreach/sdk-ui/inputs';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { useT } from '../../../../i18n/I18nProvider';

export function VerifyForm({ initialEmail, initialCode }: { initialEmail: string; initialCode: string }) {
  const t = useT();
  const { user, verifyEmail, resendVerification } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState(initialEmail || user?.email || '');
  const [code, setCode] = useState(initialCode);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);
  /** Resend could be hammered: every press minted and emailed a new code. */
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = window.setTimeout(() => setCooldown((v) => v - 1), 1000);
    return () => window.clearTimeout(id);
  }, [cooldown]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await verifyEmail(email, code.trim());
      notifySuccess(t('auth.verify.toast.done'), t('status.verified'));
      navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.common.codeMismatch'));
    } finally {
      setBusy(false);
    }
  }

  async function resend() {
    setResending(true);
    try {
      await resendVerification(email);
      setCooldown(30);
      notifySuccess(t('auth.verify.toast.resent'), t('auth.verify.toast.resentTitle'));
    } catch (err) {
      // Never let a raw server string reach the toast.
      notifyError(
        isApiError(err) && err.status >= 500
          ? t('auth.login.offline')
          : t('auth.verify.toast.resendFailed'),
        t('auth.verify.toast.resendFailedTitle')
      );
    } finally {
      setResending(false);
    }
  }

  // A user who follows the shell banner after verifying elsewhere saw the same
  // form; `user.email_verified` was right there (auth-verify-email.md §4 item 5).
  if (user?.email_verified) {
    return (
      <Stack gap="md">
        <Alert color="success" role="status">{t('auth.verify.alreadyVerified')}</Alert>
        <Button fullWidth onClick={() => navigate('/', { replace: true })}>
          {t('auth.verify.backToDashboard')}
        </Button>
      </Stack>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      <Stack gap="md">
        <TextInput
          label={t('common.email')}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.currentTarget.value)}
          required
          autoComplete="email"
        />
        <TextInput
          label={t('auth.verify.form.code')}
          value={code}
          /* Was a plain TextInput: no length cap, no digit filtering, no paste
             handling (auth-verify-email.md §4 item 7). */
          onChange={(e) => setCode(e.currentTarget.value.replace(/\D/g, '').slice(0, 6))}
          required
          maxLength={6}
          inputMode="numeric"
          autoComplete="one-time-code"
          autoFocus
        />
        {error && <Alert color="error" role="alert">{error}</Alert>}
        <Button type="submit" loading={busy} fullWidth>{t('auth.verify.submit')}</Button>
        <Group justify="center">
          <Button
            variant="subtle"
            size="xs"
            loading={resending}
            disabled={cooldown > 0}
            onClick={resend}
          >
            {cooldown > 0 ? t('auth.verify.resendIn', { seconds: cooldown }) : t('auth.verify.resend')}
          </Button>
        </Group>
        <Text role="caption" c="var(--rr-color-text-muted)" ta="center">
          {t('auth.verify.codeHint')}
        </Text>
        <Text role="caption" c="var(--rr-color-text-muted)" ta="center">
          {t('auth.verify.spamHint')}
        </Text>
      </Stack>
    </form>
  );
}
